import React, { useState } from "react";
import axios from "axios";
import "../../Style/TableStyle.scss";
import "../../Style/__searchbar.scss";
import "../../Style/__button.scss";
import "./TenantsPage.scss";
import {
  deleteAlert,
  errorAlert,
  handleSuccess,
} from "../../Components/Alerts";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { MdMapsHomeWork } from "react-icons/md";
import DataGridServerSide from "../../Components/DataGridServerSide";
import CrudPageHeader from "../../Components/CrudPageHeader";
import useAccountsGridData from "../../hooks/useAccountsGridData";
import TenantsModal from "./TenantsModal";
import TenantsViewModal from "./TenantsViewModal";

export default function TenantsPage() {
  const headers = authHeader();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [viewTenant, setViewTenant] = useState(null);

  const getApiErrorMessage = (error, fallbackMessage) => {
    if (error?.response?.data?.errorMessage) {
      return `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`;
    }

    if (
      typeof error?.response?.data === "string" &&
      error.response.data.trim() !== ""
    ) {
      return error.response.data;
    }

    if (error?.message) {
      return `Gabim: ${error.message}`;
    }

    return fallbackMessage;
  };

  const {
    loading,
    data,
    rowCount,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    quickFilter,
    setQuickFilter,
    fetchData,
  } = useAccountsGridData({
    headers,
    ledgerID: 308,
    onError: (error) =>
      errorAlert(
        getApiErrorMessage(
          error,
          "Gabim i panjohur në marrjen e qiradhënësve.",
        ),
      ),
  });

  const handleEdit = async (item) => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + `Accounts/getAccountForEdit/${item.AccountID}`,
        { headers },
      );

      setSelectedTenant(response.data);
      setShowEditModal(true);
    } catch (error) {
      errorAlert(
        getApiErrorMessage(
          error,
          "Gabim në marrjen e të dhënave të qiradhënësit për editim.",
        ),
      );
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedTenant(null);
  };

  const handleView = async (item) => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + `Accounts/getAccountForEdit/${item.AccountID}`,
        { headers },
      );

      setViewTenant(response.data);
      setShowViewModal(true);
    } catch (error) {
      errorAlert(
        getApiErrorMessage(
          error,
          "Gabim në marrjen e të dhënave të qiradhënësit për shikim.",
        ),
      );
    }
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewTenant(null);
  };

  const handleDelete = async (deleteItemId) => {
    try {
      const response = await axios.delete(
        ApiLink.API_URL + "Accounts" + `/${deleteItemId}`,
        {
          headers,
        },
      );

      const result = response.data;
      if (result.success) {
        handleSuccess("Qiradhënësi është fshirë me sukses");
        fetchData();
      } else {
        deleteAlert(
          "Qiradhënësi nuk mund të fshihet sepse është përdorur në transaksione",
        );
      }
    } catch (error) {
      deleteAlert("Ka ndodhur një gabim gjatë fshirjes së qiradhënësit.");
    }
  };

  const tenantGridHeader = [
    {
      field: "id",
      headerName: "#",
      width: 60,
      headerClassName: "super-app-theme--header",
      sortable: false,
      filterable: false,
      groupable: false,
      aggregable: false,
    },
    {
      field: "AccountName",
      headerName: "Emri i Qiradhënësit",
      flex: 2.4,
      minWidth: 300,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "IdentificationNumber",
      headerName: "Numri Identifikues",
      flex: 1.2,
      minWidth: 180,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "Adress",
      headerName: "Adresa",
      flex: 1.6,
      minWidth: 220,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "IsActive",
      headerName: "Aktiv",
      width: 90,
      headerClassName: "super-app-theme--header",
      renderCell: (params) =>
        params.value ? (
          <CheckCircleOutlineIcon style={{ color: "green" }} />
        ) : (
          ""
        ),
    },
  ];

  const rows = data.map((item, index) => ({
    id: paginationModel.page * paginationModel.pageSize + index + 1,
    AccountName: item.AccountName,
    IdentificationNumber: item.IdentificationNumber,
    Adress: item.Adress ?? "",
    IsActive: Boolean(item.IsActive),
    deleteItemId: item.AccountID,
    rowInfo: item,
  }));

  return (
    <div className="Crud__Container tenants-page__container">
      <TenantsModal
        show={showEditModal}
        onHide={handleCloseEditModal}
        mode="edit"
        initialData={selectedTenant}
        fetchData={fetchData}
      />

      <TenantsViewModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        tenantData={viewTenant}
      />

      <CrudPageHeader
        icon={MdMapsHomeWork}
        title="Lista e Qiradhënësve"
        description="Forma e Qiradhënësve: Menaxhoni, shtoni, editoni dhe fshini Qiradhënës."
        tutorialUrl="https://www.youtube.com/@Accountree"
        tutorialLabel="Shiko Tutorialin"
        action={<TenantsModal fetchData={fetchData} type="text" />}
        className="tenants-page__header"
      />

      <DataGridServerSide
        columns={tenantGridHeader}
        rows={rows}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        handleShowPrint={handleView}
        showPayment={false}
        height={500}
        rowCount={rowCount}
        loading={loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sortModel={sortModel}
        setSortModel={setSortModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        showDetailsStatus={true}
      />
    </div>
  );
}
